import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useStore from '../store/useStore';
import { fetchTemplates } from '../api/templatesApi';
import DashboardLayout from '../components/DashboardLayout';
import Logo from '../components/Logo';
import AuthModal from '../components/AuthModal';
import { useT } from '../i18n';

const FEATURE_CHIPS = [
  'Full CRUD',
  'Security',
  'Swagger',
  'DB Migration',
  'Controller',
  'Service',
  'Repository',
  'OAuth2',
];

function pickChips(templateId, count = 4) {
  // Deterministic shuffle per template id so the order is stable across renders
  // but varies between cards.
  let seed = 0;
  const key = String(templateId || '');
  for (let i = 0; i < key.length; i++) seed = (seed * 31 + key.charCodeAt(i)) | 0;
  const arr = [...FEATURE_CHIPS];
  for (let i = arr.length - 1; i > 0; i--) {
    seed = (seed * 1103515245 + 12345) & 0x7fffffff;
    const j = seed % (i + 1);
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr.slice(0, count);
}

function TemplateIcon({ kind, color }) {
  if (kind === 'blog') {
    return (
      <svg viewBox="0 0 32 32" fill="none">
        <rect x="5" y="4" width="22" height="24" rx="3" stroke={color} strokeWidth="2" fill={`${color}10`} />
        <path d="M9 10h14M9 15h14M9 20h10" stroke={color} strokeWidth="2" strokeLinecap="round" />
      </svg>
    );
  }
  if (kind === 'tasks') {
    return (
      <svg viewBox="0 0 32 32" fill="none">
        <rect x="4" y="4" width="24" height="24" rx="3" stroke={color} strokeWidth="2" fill={`${color}10`} />
        <path d="M9 11l2 2 4-4" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M17 11h6" stroke={color} strokeWidth="2" strokeLinecap="round" />
        <path d="M9 19l2 2 4-4" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M17 19h6" stroke={color} strokeWidth="2" strokeLinecap="round" />
      </svg>
    );
  }
  if (kind === 'cart') {
    return (
      <svg viewBox="0 0 32 32" fill="none">
        <path d="M5 7h3l2.5 13h13l2.5-9H10" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill={`${color}10`} />
        <circle cx="13" cy="25" r="2" fill={color} />
        <circle cx="22" cy="25" r="2" fill={color} />
      </svg>
    );
  }
  return null;
}

function TemplateCard({ template, onUse, t }) {
  const name = template.name || template.id;
  const desc = template.description || '';
  const chips = useMemo(() => pickChips(template.id, 4), [template.id]);
  return (
    <div className="template-card">
      <div className="template-card-icon" style={{ background: `${template.color}15`, color: template.color }}>
        <TemplateIcon kind={template.icon} color={template.color} />
      </div>
      <h3 className="template-card-name">{name}</h3>
      <p className="template-card-desc">{desc}</p>

      <div className="template-card-meta">
        <span className="template-meta-pill">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="2" y="2" width="10" height="3" rx="0.5" />
            <rect x="2" y="6" width="10" height="3" rx="0.5" />
          </svg>
          {template.entityCount} {t('templates.entities')}
        </span>
      </div>

      <div className="template-card-features">
        {chips.map((f) => (
          <span key={f} className="template-feature-chip">{f}</span>
        ))}
      </div>

      <button
        className="template-card-cta"
        style={{ background: template.accent }}
        onClick={() => onUse(template)}
      >
        {t('templates.use')} &rarr;
      </button>
    </div>
  );
}

function TemplatesContent({ onUse }) {
  const t = useT();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    fetchTemplates()
      .then((list) => { if (!cancelled) setTemplates(list); })
      .catch((e) => { if (!cancelled) setError(e.message || 'Failed to load'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="templates-page">
      <header className="templates-hero">
        <h1 className="templates-hero-title">{t('templates.heroTitle')}</h1>
        <p className="templates-hero-sub">{t('templates.heroSub')}</p>
      </header>

      {loading && (
        <div className="admin-loading">{t('home.loading')}</div>
      )}
      {error && !loading && (
        <div className="auth-error" style={{ maxWidth: 480, margin: '24px auto' }}>{error}</div>
      )}
      {!loading && !error && templates.length === 0 && (
        <div className="admin-empty">{t('admin.template.empty')}</div>
      )}
      {!loading && !error && templates.length > 0 && (
        <div className="templates-grid">
          {templates.map((tpl) => (
            <TemplateCard key={tpl.id} template={tpl} onUse={onUse} t={t} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function TemplatesPage() {
  const navigate = useNavigate();
  const accessToken = useStore((s) => s.accessToken);
  const theme = useStore((s) => s.theme);
  const uiLang = useStore((s) => s.uiLang);
  const setUiLang = useStore((s) => s.setUiLang);
  const toggleTheme = useStore((s) => s.toggleTheme);
  const t = useT();
  const [showAuth, setShowAuth] = useState(false);

  useEffect(() => {
    document.title = 'Backora — Templates';
  }, []);

  const handleUse = (template) => {
    if (accessToken) {
      navigate('/editor', { state: { template: template.json, templateId: template.id } });
    } else {
      navigate('/guest/view', { state: { template: template.json, templateId: template.id } });
    }
  };

  const handleAuthSuccess = () => {
    setShowAuth(false);
    navigate('/home');
  };

  // Authed: show inside DashboardLayout
  if (accessToken) {
    return (
      <DashboardLayout>
        <TemplatesContent onUse={handleUse} />
      </DashboardLayout>
    );
  }

  // Guest: standalone with landing-style nav
  return (
    <div className={`app-root ${theme}`}>
      {showAuth && (
        <AuthModal onSuccess={handleAuthSuccess} onCancel={() => setShowAuth(false)} />
      )}
      <div className="templates-standalone">
        <nav className="templates-nav">
          <Logo size={28} onClick={() => navigate('/')} style={{ cursor: 'pointer' }} />
          <div className="templates-nav-actions">
            <div className="lang-switcher">
              {['EN', 'UZ', 'RU'].map((lang) => (
                <button
                  key={lang}
                  className={`lang-btn${uiLang === lang.toLowerCase() ? ' active' : ''}`}
                  onClick={() => setUiLang(lang.toLowerCase())}
                >{lang}</button>
              ))}
            </div>
            <button className="menu-icon-btn" onClick={toggleTheme} title="Toggle theme">
              {theme === 'light' ? '☾' : '☀'}
            </button>
            <button className="landing-btn-primary" onClick={() => setShowAuth(true)}>
              {t('landing.getStarted')}
            </button>
          </div>
        </nav>
        <TemplatesContent onUse={handleUse} />
      </div>
    </div>
  );
}
